import { Text, StyleSheet, ScrollView, View, Platform } from "react-native";
import { COLORS } from "../constants";
import { useGlobalContext } from '../context/GlobalContext';
import { openDatabase } from '../database/database';
import { useEffect, useState } from "react";
import { HymnProps, AuthorProps, AuthorsHymnProps } from "../types";
import { SQLTransaction } from "expo-sqlite";

export type StanzasProps = {
  id: number;
  number: number;
  text: string;
  time: 0
}

function initFavorites (tx: SQLTransaction, hymns: HymnProps[], setFavorites: (hymns: HymnProps[]) => void) {
  tx.executeSql(
    'select * from favorites;',
    [],
    (_, { rows: { _array } }) => {
      const items: HymnProps[] = [];
      _array.forEach(item => {
        const hymn = hymns.find(h => h.id === item.hymn_id)
        if (hymn) {
          items.push(hymn);
        }
      })
      setFavorites(items);
    }
  );
}

const MAX_TEXT = 36;

const setAuthorsHymn = (item: HymnProps, authors: Record<number, AuthorProps>) => {
  const author: AuthorsHymnProps = {}
  if (item.authors) {
    author.authors = JSON.parse(item.authors).map((id: number) => authors[id].name).join(', ');
    author.text = author.authors;
  } else {
    if (item.music_id) {
      author.music = authors[item.music_id].name;
      author.text = author.music;
    }
    if (item.letter_id) {
      if (item.music_id !== item.letter_id) {
        author.letter = authors[item.letter_id].name;
        author.text += `, ${author.letter}`;
      }
    }
  }
  if (author.text && author.text.length > MAX_TEXT) {
    author.text = author.text?.substring(0, MAX_TEXT) + '...';
  }
  item.author = author;
}

function Letters () {
  const { hymnId, hymns, setHymns, setAuthors, setFavorites } = useGlobalContext()
  const [hymn, setHymn] = useState<HymnProps>()
  const [stanzas, setStanzas] = useState<StanzasProps[]>([]);
  
  useEffect(() => {
    (async () => {
      const db = await openDatabase();
  
      db.transaction((tx) => {
        if (hymns.length === 0) {
          tx.executeSql(
            'select * from authors;',
            [],
            (_, { rows: { _array } }) => {
              const newAuthors: Record<number, AuthorProps> = {};
              _array.forEach((item: AuthorProps) => (newAuthors[item.id] = item));
              setAuthors(newAuthors);
              tx.executeSql(
                'select * from hymns;',
                [],
                (_, { rows: { _array } }) => {
                  const newHymns: HymnProps[] = _array;
                  newHymns.forEach(row => setAuthorsHymn(row, newAuthors));
                  setHymns(newHymns);
                  setHymn(newHymns.find(hymn => hymn.id === hymnId));
                  if (Platform.OS === 'web') {
                    if (!window.localStorage.getItem('favorites')) {
                      window.localStorage.setItem('favorites', JSON.stringify([]));
                    }
                    setFavorites(JSON.parse(window.localStorage.getItem('favorites') || JSON.stringify([])))
                  } else {
                    initFavorites(tx, newHymns, setFavorites);
                  }
                }
              );
            }
          );
        } else {
          setHymn(hymns.find(hymn => hymn.id === hymnId));
          if (Platform.OS === 'web') {
            setFavorites(JSON.parse(window.localStorage.getItem('favorites') || JSON.stringify([])))
          } else {
            initFavorites(tx, hymns, setFavorites);
          }
        }
        setStanzas([]);
        tx.executeSql(
          'select * from stanzas where hymn_id=?;',
          [hymnId],
          (_, { rows: { _array } }) => setStanzas(_array)
        );
      });
    })()
  }, [hymnId]);

  const getAuthors = (item: HymnProps) => {
    if (item.music_id && item.letter_id && item.music_id == item.letter_id) {
      return <Text style={styles.author}>{item.author?.music}</Text>
    }
    if (item.authors) {
      return <Text style={styles.author}>{item.author?.authors}</Text>
    }
    return (
      <>
        <Text style={styles.author}><Text style={styles.bold}>Música: </Text> {item.author?.music}</Text>
        <Text style={styles.author}><Text style={styles.bold}>Letra: </Text> {item.author?.letter}</Text>
      </>
    )
  }

  const getTextChoir = (item: StanzasProps, number: number) => {
    let texti18n = 'Coro';
    if ([302, 305, 306, 307, 308, 309, 310, 311, 313, 315].includes(number)) {
      texti18n = 'Refrain';
    }
    if (item.number === 0) {
      return <Text style={styles.stanzaChoir}>{texti18n}</Text>
    }
    if (item.number >= 100 && item.number < 200) {
      return <Text style={styles.stanzaChoir}>{texti18n} {String(item.number).substring(2)}</Text>
    }
    return <></>
  }

  const getTextNumber = (item: StanzasProps) => {
    if (item.number === 0 || item.number > 100 || item.number === -2) {
      return <></>
    }
    return <Text style={styles.bold}>{item.number}. </Text>
  }

  return (
    <ScrollView style={styles.container}>
      {hymn && <View>
        <Text style={styles.number}>{hymn.number}</Text>
        <Text style={styles.title}>{hymn.title}</Text>
        <Text style={styles.tone}>
          <Text style={styles.bold}>Tono: </Text>{hymn.tone} <Text style={styles.bold}> ‧ </Text> {hymn.compass} <Text style={styles.bold}> ‧ </Text> {hymn.verse}
        </Text>
        {stanzas.filter(item => item.number !== -1).map(item => (
          <View style={styles.stanza} key={item.number}>
            {getTextChoir(item, hymn.number)}
            <Text style={styles.stanzaText}>
              {getTextNumber(item)}
              {item.text.trim()}
            </Text>
          </View>
        ))}
        {stanzas.filter(item => item.number === -1).map(item => (
          <View style={styles.stanza} key={item.number}>
            <Text style={styles.stanzaText}>
              {item.text.trim()}
            </Text>
          </View>
        ))}
        <View style={styles.containerAuthor}>
          { getAuthors(hymn) }
        </View>
      </View>}
    </ScrollView>
  )  
}

const styles = StyleSheet.create({
  container: {
  },
  containerNumber: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  containerAuthor: {
    paddingHorizontal: 15,
    marginTop: 20,
    marginBottom: 5
  },
  number: {
    textAlign: 'right',
    fontFamily: 'PoppinsBold',
    color: COLORS.text,
    fontSize: 28,
    paddingRight: 15,
    paddingTop: 5,
  },
  title: {
    textAlign: 'center',
    fontFamily: 'PoppinsBold',
    color: COLORS.primary,
    fontSize: 18,
    marginTop: -5
  },
  tone: {
    textAlign: 'center',
    fontFamily: 'Poppins',
    color: COLORS.text,
    fontSize: 12,
    marginTop: -5,
    marginBottom: 20,
  },
  author: {
    fontFamily: 'Poppins',
    color: COLORS.text,
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'right',
    lineHeight: 18
  },
  stanza: {
    marginBottom: 25,
    paddingHorizontal: 15
  },
  stanzaText: {
    textAlign: 'center',
    fontFamily: 'Poppins',
    color: COLORS.text,
    fontSize: 16,
  },
  stanzaChoir: {
    textAlign: 'center',
    fontFamily: 'PoppinsBold',
    fontSize: 14,
  },
  bold: {
    fontFamily: 'PoppinsBold',
  },
});

export default Letters;
